import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { TRUST_LOGOS } from "@/lib/brand";

const stats = [
  { v: "500+", k: "Machines Sold" },
  { v: "25+", k: "Cities Served" },
  { v: "99%", k: "Avg Uptime" },
  { v: "10+", k: "Years of Expertise" },
];

const testimonials = [
  {
    quote:
      "We bought four machines outright. Payback was faster than projected and the AMC means we don&apos;t think about service.",
    name: "Head of Workplace, IT Park (Vadodara)",
  },
  {
    quote:
      "PPE compliance jumped after we installed Refuel SAFE on the shop floor. Buying the machines was the right call.",
    name: "EHS Manager, Manufacturing Plant",
  },
];

export default function SocialProof() {
  return (
    <section id="proof" data-testid="proof-section" className="relative py-24 md:py-32 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-12">
          <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-600 mb-3">
            Customer Stories
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-950">
            Numbers we&apos;ve earned.
            <br />
            <span className="text-zinc-500">Words from people we work with.</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          {stats.map((s, i) => (
            <motion.div
              key={s.k}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="rounded-2xl border border-zinc-200 bg-white p-6 md:p-8"
              data-testid={`proof-stat-${i}`}
            >
              <div className="font-display text-4xl md:text-5xl font-bold text-zinc-950 tracking-tighter">
                {s.v}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                {s.k}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust line + restored partner logo marquee */}
        <div className="mb-16" data-testid="trust-line">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 text-center mb-6">
            Trusted across India
          </div>
          <div className="relative overflow-hidden mask-fade" data-testid="client-logos">
            <div className="flex gap-12 md:gap-16 animate-marquee whitespace-nowrap items-center">
              {[...TRUST_LOGOS, ...TRUST_LOGOS, ...TRUST_LOGOS].map((c, i) => (
                <div
                  key={`${c.name}-${i}`}
                  className="shrink-0 h-16 md:h-20 flex items-center"
                  title={c.name}
                >
                  <img
                    src={c.url}
                    alt={c.name}
                    className="h-12 md:h-14 w-auto object-contain opacity-70 hover:opacity-100 transition"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      if (c.fallback && e.currentTarget.src !== c.fallback) {
                        e.currentTarget.src = c.fallback;
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`testimonial-${i}`}
              className="relative rounded-3xl border border-zinc-200 bg-white p-8 md:p-10"
            >
              <Quote className="w-7 h-7 text-emerald-600 mb-5" />
              <p
                className="font-display text-xl md:text-2xl font-medium text-zinc-950 leading-snug tracking-tight"
                dangerouslySetInnerHTML={{ __html: `“${t.quote}”` }}
              />
              <div className="mt-6 text-sm text-zinc-500">— {t.name}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
